'use client'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useSocketContext } from '@/context/SocketProvider'
import { isTerminalTicketStatus, shouldClearFollowUpDraft } from '@/lib/ticket-follow-up'
import { TicketTimelineEntry, WidgetTicket, useChatStore } from '@/store/chat.store'
import { useSocketStore } from '@/store/socket.store'
import { cn } from '@/lib/utils'
import { ArrowLeft, ChevronRight, Loader2, TicketIcon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'
import MarkdownMessage from '../markdown-message'

const STATUS_BADGE_CLASSES: Record<string, string> = {
  open: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  resolved: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  closed: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
}

function StatusBadge({ ticket }: { ticket: WidgetTicket }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-full border border-current/10 px-3 py-1 text-[11px] font-medium',
        STATUS_BADGE_CLASSES[ticket.status] || 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
      )}
    >
      {ticket.status_label}
    </span>
  )
}

function TimelineEntry({ entry, locale }: { entry: TicketTimelineEntry; locale: string }) {
  const t = useTranslations('tickets')
  const isUser = entry.role === 'user'

  return (
    <div className={cn('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl border border-current/20 px-3 py-2 text-sm break-words',
          isUser
            ? 'bg-[var(--widget-visitor-bubble)] text-[var(--widget-visitor-text)]'
            : 'bg-[var(--widget-operator-bubble)] text-[var(--widget-operator-text)]'
        )}
      >
        <MarkdownMessage content={entry.content} />
      </div>
      <span className='text-muted-foreground px-1 text-[10px]'>
        {isUser ? t('you') : t('operator')}
        {entry.timestamp ? ` · ${new Date(entry.timestamp).toLocaleString(locale)}` : ''}
      </span>
    </div>
  )
}

export default function TicketsPanel() {
  const t = useTranslations('tickets')
  const locale = useLocale()
  const { requestTickets, requestTicketHistory, sendTicketFollowUp } = useSocketContext()
  const [followUpContent, setFollowUpContent] = useState('')
  const wasSendingTicketFollowUpRef = useRef(false)

  const tickets = useChatStore(state => state.tickets)
  const isLoadingTickets = useChatStore(state => state.isLoadingTickets)
  const activeTicket = useChatStore(state => state.activeTicket)
  const isLoadingTicketHistory = useChatStore(state => state.isLoadingTicketHistory)
  const ticketHistoryError = useChatStore(state => state.ticketHistoryError)
  const isSendingTicketFollowUp = useChatStore(state => state.isSendingTicketFollowUp)
  const ticketFollowUpError = useChatStore(state => state.ticketFollowUpError)
  const setActiveTicket = useChatStore(state => state.setActiveTicket)
  const setTicketHistoryError = useChatStore(state => state.setTicketHistoryError)
  const setTicketFollowUpError = useChatStore(state => state.setTicketFollowUpError)
  const setIsSendingTicketFollowUp = useChatStore(state => state.setIsSendingTicketFollowUp)
  const ticketsRefreshKey = useChatStore(state => state.ticketsRefreshKey)

  const socketConnectionStatus = useSocketStore(state => state.socketConnectionStatus)

  // Fetch on mount and whenever a notification frame signals ticket activity.
  // An open ticket detail refreshes silently so the member sees new updates
  // without navigating back to the list.
  useEffect(() => {
    if (socketConnectionStatus === 'opened') {
      requestTickets()
      const openTicketId = useChatStore.getState().activeTicket?.ticket.id
      if (openTicketId) {
        requestTicketHistory(openTicketId, { silent: true })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsRefreshKey, socketConnectionStatus])

  const closeDetail = () => {
    setActiveTicket(null)
    setTicketHistoryError(false)
    setTicketFollowUpError(false)
    setIsSendingTicketFollowUp(false)
    setFollowUpContent('')
  }

  const showFollowUpComposer =
    activeTicket && !isLoadingTicketHistory && !ticketHistoryError && isTerminalTicketStatus(activeTicket.ticket.status)

  useEffect(() => {
    if (shouldClearFollowUpDraft(wasSendingTicketFollowUpRef.current, isSendingTicketFollowUp, ticketFollowUpError)) {
      setFollowUpContent('')
    }
    wasSendingTicketFollowUpRef.current = isSendingTicketFollowUp
  }, [isSendingTicketFollowUp, ticketFollowUpError])

  const handleSendFollowUp = () => {
    const content = followUpContent.trim()
    if (!activeTicket || !content || isSendingTicketFollowUp) return
    sendTicketFollowUp(activeTicket.ticket.id, content)
  }

  const showDetail = isLoadingTicketHistory || ticketHistoryError || activeTicket

  if (showDetail) {
    return (
      <div className='flex flex-1 flex-col overflow-hidden'>
        <div className='flex shrink-0 items-center gap-2 border-b px-3 py-2'>
          <button
            onClick={closeDetail}
            className='text-muted-foreground hover:text-foreground hover:bg-muted flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all active:scale-95'
            title={t('back')}
          >
            <ArrowLeft className='size-4' />
          </button>
          {activeTicket && (
            <>
              <p className='min-w-0 flex-1 truncate text-sm font-medium'>{activeTicket.ticket.subject || t('title')}</p>
              <StatusBadge ticket={activeTicket.ticket} />
            </>
          )}
        </div>

        <div className='flex-1 overflow-y-auto px-4 py-3'>
          {isLoadingTicketHistory ? (
            <div className='text-muted-foreground flex items-center justify-center gap-2 py-8 text-xs font-medium'>
              <Loader2 className='size-4 animate-spin' />
              <p>{t('loading')}</p>
            </div>
          ) : ticketHistoryError ? (
            <p className='text-muted-foreground py-8 text-center text-sm'>{t('historyError')}</p>
          ) : activeTicket && activeTicket.messages.length > 0 ? (
            <div className='flex flex-col gap-3'>
              {activeTicket.messages.map(entry => (
                <TimelineEntry key={entry.id || `${entry.role}-${entry.timestamp}`} entry={entry} locale={locale} />
              ))}
            </div>
          ) : (
            <p className='text-muted-foreground py-8 text-center text-sm'>{t('noMessages')}</p>
          )}
        </div>

        {showFollowUpComposer && (
          <div className='flex shrink-0 flex-col gap-2 border-t px-4 py-3'>
            <p className='text-muted-foreground mt-1 text-[11px]'>{t('followUpHint')}</p>
            <Textarea
              value={followUpContent}
              onChange={event => setFollowUpContent(event.target.value)}
              placeholder={t('followUpPlaceholder')}
              disabled={isSendingTicketFollowUp}
              rows={3}
              className='min-h-20 resize-none'
            />
            {ticketFollowUpError && <p className='text-destructive text-xs'>{t('followUpError')}</p>}
            <Button
              onClick={handleSendFollowUp}
              disabled={isSendingTicketFollowUp || followUpContent.trim().length === 0}
              className='w-full'
            >
              {isSendingTicketFollowUp ? t('followUpSending') : t('followUpSend')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='flex-1 overflow-y-auto overscroll-contain p-5'>
      {isLoadingTickets && tickets.length === 0 ? (
        <div className='text-muted-foreground flex items-center justify-center gap-2 py-8 text-xs font-medium'>
          <Loader2 className='size-4 animate-spin' />
          <p>{t('loading')}</p>
        </div>
      ) : tickets.length === 0 ? (
        <div className='text-muted-foreground flex flex-col items-center justify-center gap-2 py-10 text-center'>
          <TicketIcon className='size-8 opacity-40' />
          <p className='px-6 text-sm'>{t('empty')}</p>
        </div>
      ) : (
        <div className='flex flex-col gap-4'>
          {tickets.map(ticket => (
            <button
              key={ticket.id}
              onClick={() => requestTicketHistory(ticket.id)}
              className='group hover:border-primary/40 flex cursor-pointer items-center gap-2 rounded-2xl border p-3.5 text-start transition-all hover:shadow-md active:scale-[0.99]'
            >
              <span className='bg-muted/30 text-foreground group-hover:text-primary flex size-11 shrink-0 items-center justify-center rounded-full border'>
                <TicketIcon className='size-4' />
              </span>
              <div className='min-w-0 flex-1'>
                <p className='text-muted-foreground truncate text-[11px] font-semibold' title={ticket.id}>
                  #{ticket.id}
                </p>
                <p className='text-sm leading-snug font-bold break-words'>{ticket.subject || t('title')}</p>
                {ticket.updated_at && (
                  <p className='text-muted-foreground mt-1 text-[11px]'>
                    {new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
                      ticket.updated_at
                    )}
                  </p>
                )}
              </div>
              <StatusBadge ticket={ticket} />
              <ChevronRight className='text-muted-foreground size-4 shrink-0' />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
